import { Navigate } from "react-router";
import type { AppRouteObject } from "./types";
import { lazyRoute } from "./lazyRoute";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import * as Dtos from "src/dtos/Tmd.dtos";

export const settingsRoutes = {
    path: "settings",
    handle: {
        title: "Settings",
        help: null,
        access: { permission: Dtos.Permission.SettingsView },
        breadcrumb: { label: "Settings", icon: FontAwesomeIcons.Duotone.SETTINGS, url: "/settings" },
        navigation: { title: "Settings", icon: FontAwesomeIcons.Duotone.SETTINGS, primary: true, drawer: true, order: 8 },
    },
    children: [
        {
            index: true,
            lazy: lazyRoute(() => import("../containers/Settings/SettingsPage")),
            handle: {
                title: "Settings",
                help: { topicKey: "settings" },
                access: { permission: Dtos.Permission.SettingsView },
                breadcrumb: { label: "Settings", icon: FontAwesomeIcons.Duotone.SETTINGS, url: "/settings" },
            }
        },
        {
            path: "general",
            handle: {
                title: "General",
                help: null,
                access: { permission: Dtos.Permission.GeneralSettingsView },
                breadcrumb: { label: "General", icon: FontAwesomeIcons.Duotone.COG, url: "/settings/general" },
                navigation: { title: "General", description: "Manage general system settings, including roles and professional disciplines.", icon: FontAwesomeIcons.Duotone.COG, drawer: true },
            },
            children: [
                {
                    index: true,
                    lazy: lazyRoute(() => import("../containers/Settings/GeneralSettings/GeneralSettingsPage")),
                    handle: {
                        title: "General",
                        help: { topicKey: "general-settings" },
                        access: { permission: Dtos.Permission.GeneralSettingsView },
                        breadcrumb: { label: "General", icon: FontAwesomeIcons.Duotone.COG, url: "/settings/general" },
                    }
                },
                {
                    path: "countries",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Lookups/Countries/CountriesPage")),
                            handle: { help: { topicKey: "countries" } }
                        },
                        {
                            path: ":countryId",
                            lazy: lazyRoute(() => import("../containers/Lookups/Country/CountryPage")),
                            handle: { help: { topicKey: "country" } }
                        },
                    ],
                },
                {
                    path: "roles",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Lookups/Roles/RolesPage")),
                            handle: { help: { topicKey: "roles" } }
                        },
                        {
                            path: ":roleId",
                            lazy: lazyRoute(() => import("../containers/Lookups/Role/RolePage")),
                            handle: { help: { topicKey: "role" } }
                        },
                    ],
                },
                {
                    path: "states",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Lookups/States/StatesPage")),
                            handle: { help: { topicKey: "states" } }
                        },
                        {
                            path: ":stateId",
                            lazy: lazyRoute(() => import("../containers/Lookups/State/StatePage")),
                            handle: { help: { topicKey: "state" } }
                        },
                    ],
                },
                {
                    path: "itemcomponenttypes",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Lookups/ItemComponentTypes/ItemComponentTypesPage")),
                            handle: { help: { topicKey: "item-component-types" } }
                        },
                        {
                            path: ":itemcomponenttypesId",
                            lazy: lazyRoute(() => import("../containers/Lookups/ItemComponentType/ItemComponentTypePage")),
                            handle: { help: { topicKey: "item-component-type" } }
                        },
                    ],
                },
                {
                    path: "notetypes",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Lookups/NoteTypes/NoteTypesPage")),
                            handle: { help: { topicKey: "note-types" } }
                        },
                        {
                            path: ":notetypesId",
                            lazy: lazyRoute(() => import("../containers/Lookups/NoteType/NoteTypePage")),
                            handle: { help: { topicKey: "note-type" } }
                        },
                    ],
                },
                {
                    path: "interests",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Lookups/Interests/InterestsPage")),
                            handle: { help: { topicKey: "interests" } }
                        },
                        {
                            path: ":interestId",
                            lazy: lazyRoute(() => import("../containers/Lookups/Interest/InterestPage")),
                            handle: { help: { topicKey: "interest" } }
                        },
                    ],
                },
                {
                    path: "membershiptypes",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Lookups/MembershipTypes/MembershipTypesPage")),
                            handle: { help: { topicKey: "membership-types" } }
                        },
                        {
                            path: ":membershiptypesId",
                            lazy: lazyRoute(() => import("../containers/Lookups/MembershipType/MembershipTypePage")),
                            handle: { help: { topicKey: "membership-type" } }
                        },
                    ],
                },
                {
                    path: "professionaldisciplines",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Lookups/ProfessionalDisciplines/ProfessionalDisciplinesPage")),
                            handle: { help: { topicKey: "professional-disciplines" } }
                        },
                        {
                            path: ":professionaldisciplinesId",
                            lazy: lazyRoute(() => import("../containers/Lookups/ProfessionalDiscipline/ProfessionalDisciplinePage")),
                            handle: { help: { topicKey: "professional-discipline" } }
                        },
                    ],
                },
                {
                    path: "salutations",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Lookups/Salutations/SalutationsPage")),
                            handle: { help: { topicKey: "salutations" } }
                        },
                        {
                            path: ":salutationId",
                            lazy: lazyRoute(() => import("../containers/Lookups/Salutation/SalutationPage")),
                            handle: { help: { topicKey: "salutation" } }
                        },
                    ],
                },
            ],
        },
        {
            path: "project",
            handle: {
                title: "Project",
                help: null,
                access: { permission: Dtos.Permission.ProjectView },
                breadcrumb: { label: "Project", icon: FontAwesomeIcons.Duotone.CLIPBOARD, url: "/settings/project" },
                navigation: { title: "Project", description: "Configure project related templates and settings.", icon: FontAwesomeIcons.Duotone.CLIPBOARD, drawer: true },
            },
            children: [
                {
                    index: true,
                    lazy: lazyRoute(() => import("../containers/Settings/ProjectSettings/ProjectSettingsPage")),
                    handle: {
                        title: "Project",
                        help: { topicKey: "project-settings" },
                        access: { permission: Dtos.Permission.ProjectSettingsView },
                        breadcrumb: { label: "Project", icon: FontAwesomeIcons.Duotone.CLIPBOARD, url: "/settings/project" },
                    }
                },
                {
                    path: "itemtemplates",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/ProjectLookups/ProjectItemTemplates/ProjectItemTemplatesPage")),
                            handle: { help: { topicKey: "project-item-templates" } }
                        },
                        {
                            path: ":projectItemTemplateId",
                            lazy: lazyRoute(() => import("../containers/ProjectLookups/ProjectItemTemplate/ProjectItemTemplatePage")),
                            handle: { help: { topicKey: "project-item-template" } }
                        },
                    ],
                },
                {
                    path: "itemcomponents",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponents/ItemComponentsPage"), { context: "project" }),
                            handle: { help: { topicKey: "item-components" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "project", mode: "create" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                        {
                            path: "create/:cloneId",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "project", mode: "clone" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                        {
                            path: ":itemComponentId",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "project", mode: "edit" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                    ],
                },
                {
                    path: "itemCategories",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/ProjectLookups/ProjectItemTemplateCategorys/ProjectItemTemplateCategorysPage")),
                            handle: { help: { topicKey: "project-item-template-categorys" } }
                        },
                        {
                            path: ":projectItemTemplateCategoryId",
                            lazy: lazyRoute(() => import("../containers/ProjectLookups/ProjectItemTemplateCategory/ProjectItemTemplateCategoryPage")),
                            handle: { help: { topicKey: "project-item-template-category" } }
                        },
                    ],
                },
            ],
        },
        {
            path: "trial",
            handle: {
                title: "Trial",
                help: null,
                access: { permission: Dtos.Permission.TrialSettingsView },
                breadcrumb: { label: "Trial", icon: FontAwesomeIcons.Duotone.TRIAL, url: "/settings/trial" },
                navigation: { title: "Trial", description: "Manage trial related templates and settings.", icon: FontAwesomeIcons.Duotone.TRIAL, drawer: true },
            },
            children: [
                {
                    index: true,
                    lazy: lazyRoute(() => import("../containers/Settings/TrialSettings/TrialSettingsPage")),
                    handle: {
                        title: "Trial",
                        help: { topicKey: "trial-settings" },
                        access: { permission: Dtos.Permission.TrialSettingsView },
                        breadcrumb: { label: "Trial", icon: FontAwesomeIcons.Duotone.TRIAL, url: "/settings/trial" },
                    }
                },
                {
                    path: "itemtemplates",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/TrialLookups/TrialItemTemplates/TrialItemTemplatesPage")),
                            handle: { help: { topicKey: "trial-item-templates" } }
                        },
                        {
                            path: ":trialId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    lazy: lazyRoute(() => import("../containers/TrialLookups/TrialItemTemplates/TrialItemTemplatesPage")),
                                    handle: { help: { topicKey: "trial-item-templates" } }
                                },
                                {
                                    path: "template",
                                    handle: { help: null },
                                    children: [
                                        {
                                            path: ":trialItemTemplateId",
                                            lazy: lazyRoute(() => import("../containers/TrialLookups/TrialItemTemplate/TrialItemTemplatePage")),
                                            handle: { help: { topicKey: "trial-item-template" } }
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "template",
                            handle: { help: null },
                            children: [
                                {
                                    path: ":trialItemTemplateId",
                                    lazy: lazyRoute(() => import("../containers/TrialLookups/TrialItemTemplate/TrialItemTemplatePage")),
                                    handle: { help: { topicKey: "trial-item-template" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "itemcomponents",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponents/ItemComponentsPage"), { context: "trial" }),
                            handle: { help: { topicKey: "item-components" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "trial", mode: "create" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                        {
                            path: "create/:cloneId",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "trial", mode: "clone" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                        {
                            path: ":itemComponentId",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "trial", mode: "edit" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                    ],
                },
                {
                    path: "itemtemplatecategories",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/TrialLookups/TrialItemTemplateCategories/TrialItemTemplateCategoriesPage")),
                            handle: { help: { topicKey: "trial-item-template-categories" } }
                        },
                        {
                            path: ":trialItemTemplateCategoryId",
                            lazy: lazyRoute(() => import("../containers/TrialLookups/TrialItemTemplateCategory/TrialItemTemplateCategoryPage")),
                            handle: { help: { topicKey: "trial-item-template-category" } }
                        },
                    ],
                },
                {
                    path: "trialtypes",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/TrialLookups/TrialTypes/TrialTypesPage")),
                            handle: { help: { topicKey: "trial-types" } }
                        },
                        {
                            path: ":trialTypeId",
                            lazy: lazyRoute(() => import("../containers/TrialLookups/TrialType/TrialTypePage")),
                            handle: { help: { topicKey: "trial-type" } }
                        },
                    ],
                },
            ],
        },
        {
            path: "trialinstitution",
            handle: {
                title: "Trial Institution",
                help: null,
                access: { permission: Dtos.Permission.TrialInstitutionSettingsView },
                breadcrumb: { label: "Trial Institution", icon: FontAwesomeIcons.Duotone.TRIALINSTITUTION, url: "/settings/trialinstitution" },
                navigation: { title: "Trial Institution", description: "Manage trial institution related templates and settings.", icon: FontAwesomeIcons.Duotone.TRIALINSTITUTION, drawer: true },
            },
            children: [
                {
                    index: true,
                    lazy: lazyRoute(() => import("../containers/Settings/TrialInstitutionSettings/TrialInstitutionSettingsPage")),
                    handle: {
                        title: "Trial Institution",
                        help: { topicKey: "trial-institution-settings" },
                        access: { permission: Dtos.Permission.TrialInstitutionSettingsView },
                        breadcrumb: { label: "Trial Institution", icon: FontAwesomeIcons.Duotone.TRIALINSTITUTION, url: "/settings/trialinstitution" },
                    }
                },
                {
                    path: "itemtemplates",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/TrialInstitutionLookups/TrialInstitutionItemTemplates/TrialInstitutionItemTemplatesPage")),
                            handle: { help: { topicKey: "trial-institution-item-templates" } }
                        },
                        {
                            path: ":trialId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    lazy: lazyRoute(() => import("../containers/TrialInstitutionLookups/TrialInstitutionItemTemplates/TrialInstitutionItemTemplatesPage")),
                                    handle: { help: { topicKey: "trial-institution-item-templates" } }
                                },
                                {
                                    path: "template",
                                    handle: { help: null },
                                    children: [
                                        {
                                            path: ":trialInstitutionItemTemplateId",
                                            lazy: lazyRoute(() => import("../containers/TrialInstitutionLookups/TrialInstitutionItemTemplate/TrialInstitutionItemTemplatePage")),
                                            handle: { help: { topicKey: "trial-institution-item-template" } }
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "template",
                            handle: { help: null },
                            children: [
                                {
                                    path: ":trialInstitutionItemTemplateId",
                                    lazy: lazyRoute(() => import("../containers/TrialInstitutionLookups/TrialInstitutionItemTemplate/TrialInstitutionItemTemplatePage")),
                                    handle: { help: { topicKey: "trial-institution-item-template" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "itemcomponents",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponents/ItemComponentsPage"), { context: "trialInstitution" }),
                            handle: { help: { topicKey: "item-components" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "trialInstitution", mode: "create" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                        {
                            path: "create/:cloneId",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "trialInstitution", mode: "clone" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                        {
                            path: ":itemComponentId",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "trialInstitution", mode: "edit" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                    ],
                },
                {
                    path: "itemtemplatecategories",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/TrialInstitutionLookups/TrialInstitutionItemTemplateCategories/TrialInstitutionItemTemplateCategoriesPage")),
                            handle: { help: { topicKey: "trial-institution-item-template-categories" } }
                        },
                        {
                            path: ":trialInstitutionItemTemplateCategoryId",
                            lazy: lazyRoute(() => import("../containers/TrialInstitutionLookups/TrialInstitutionItemTemplateCategory/TrialInstitutionItemTemplateCategoryPage")),
                            handle: { help: { topicKey: "trial-institution-item-template-category" } }
                        },
                    ],
                },
            ],
        },
        {
            path: "trialgroup",
            handle: {
                title: "Trial Group",
                help: null,
                access: { permission: Dtos.Permission.TrialGroupSettingsView },
                breadcrumb: { label: "Trial Group", icon: FontAwesomeIcons.Duotone.TRIALGROUP, url: "/settings/trialgroup" },
                navigation: { title: "Trial Group", description: "Manage trial group related templates and settings.", icon: FontAwesomeIcons.Duotone.TRIALGROUP, drawer: true },
            },
            children: [
                {
                    index: true,
                    lazy: lazyRoute(() => import("../containers/Settings/TrialGroupSettings/TrialGroupSettingsPage")),
                    handle: {
                        title: "Trial Group",
                        help: { topicKey: "trial-group-settings" },
                        access: { permission: Dtos.Permission.TrialGroupSettingsView },
                        breadcrumb: { label: "Trial Group", icon: FontAwesomeIcons.Duotone.TRIALGROUP, url: "/settings/trialgroup" },
                    }
                },
                {
                    path: "itemtemplates",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/TrialGroupLookups/TrialGroupItemTemplates/TrialGroupItemTemplatesPage")),
                            handle: { help: { topicKey: "trial-group-item-templates" } }
                        },
                        {
                            path: ":groupId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    lazy: lazyRoute(() => import("../containers/TrialGroupLookups/TrialGroupItemTemplates/TrialGroupItemTemplatesPage")),
                                    handle: { help: { topicKey: "trial-group-item-templates" } }
                                },
                                {
                                    path: "template",
                                    handle: { help: null },
                                    children: [
                                        {
                                            path: ":trialGroupItemTemplateId",
                                            lazy: lazyRoute(() => import("../containers/TrialGroupLookups/TrialGroupItemTemplate/TrialGroupItemTemplatePage")),
                                            handle: { help: { topicKey: "trial-group-item-template" } }
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "template",
                            handle: { help: null },
                            children: [
                                {
                                    path: ":trialGroupItemTemplateId",
                                    lazy: lazyRoute(() => import("../containers/TrialGroupLookups/TrialGroupItemTemplate/TrialGroupItemTemplatePage")),
                                    handle: { help: { topicKey: "trial-group-item-template" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "itemcomponents",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponents/ItemComponentsPage"), { context: "trialGroup" }),
                            handle: { help: { topicKey: "item-components" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "trialGroup", mode: "create" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                        {
                            path: "create/:cloneId",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "trialGroup", mode: "clone" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                        {
                            path: ":itemComponentId",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "trialGroup", mode: "edit" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                    ],
                },
                {
                    path: "itemtemplatecategories",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/TrialGroupLookups/TrialGroupItemTemplateCategories/TrialGroupItemTemplateCategoriesPage")),
                            handle: { help: { topicKey: "trial-group-item-template-categories" } }
                        },
                        {
                            path: ":trialGroupItemTemplateCategoryId",
                            lazy: lazyRoute(() => import("../containers/TrialGroupLookups/TrialGroupItemTemplateCategory/TrialGroupItemTemplateCategoryPage")),
                            handle: { help: { topicKey: "trial-group-item-template-category" } }
                        },
                    ],
                },
            ],
        },
        {
            path: "group",
            handle: {
                title: "Group",
                help: null,
                access: { permission: Dtos.Permission.GroupSettingsView },
                breadcrumb: { label: "Group", icon: FontAwesomeIcons.Duotone.GROUP, url: "/settings/group" },
                navigation: { title: "Group", description: "Set up and manage research or study groups.", icon: FontAwesomeIcons.Duotone.GROUP, drawer: true },
            },
            children: [
                {
                    index: true,
                    lazy: lazyRoute(() => import("../containers/Settings/GroupSettings/GroupSettingsPage")),
                    handle: {
                        title: "Group",
                        help: { topicKey: "group-settings" },
                        access: { permission: Dtos.Permission.GroupSettingsView },
                        breadcrumb: { label: "Group", icon: FontAwesomeIcons.Duotone.GROUP, url: "/settings/group" },
                    },
                },
                {
                    path: "itemtemplates",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/GroupSettings/GroupItemTemplates/GroupItemTemplatesPage")),
                            handle: { help: { topicKey: "group-item-templates" } }
                        },
                        {
                            path: ":groupId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    lazy: lazyRoute(() => import("../containers/GroupSettings/GroupItemTemplates/GroupItemTemplatesPage")),
                                    handle: { help: { topicKey: "group-item-templates" } }
                                },
                                {
                                    path: "template",
                                    handle: { help: null },
                                    children: [
                                        {
                                            path: ":groupItemTemplateId",
                                            lazy: lazyRoute(() => import("../containers/GroupSettings/GroupItemTemplate/GroupItemTemplatePage")),
                                            handle: { help: { topicKey: "group-item-template" } }
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "template",
                            handle: { help: null },
                            children: [
                                {
                                    path: ":groupItemTemplateId",
                                    lazy: lazyRoute(() => import("../containers/GroupSettings/GroupItemTemplate/GroupItemTemplatePage")),
                                    handle: { help: { topicKey: "group-item-template" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "itemcomponents",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponents/ItemComponentsPage"), { context: "group" }),
                            handle: { help: { topicKey: "item-components" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "group", mode: "create" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                        {
                            path: "create/:cloneId",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "group", mode: "clone" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                        {
                            path: ":itemComponentId",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "group", mode: "edit" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                    ],
                },
                {
                    path: "itemCategories",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/GroupSettings/GroupItemTemplateCategorys/GroupItemTemplateCategorysPage")),
                            handle: { help: { topicKey: "group-item-template-categorys" } }
                        },
                        {
                            path: ":groupItemTemplateCategoryId",
                            lazy: lazyRoute(() => import("../containers/GroupSettings/GroupItemTemplateCategory/GroupItemTemplateCategoryPage")),
                            handle: { help: { topicKey: "group-item-template-category" } }
                        },
                    ],
                },
            ],
        },
        {
            path: "institution",
            handle: {
                title: "Institution",
                help: null,
                access: { permission: Dtos.Permission.InstitutionSettingsView },
                breadcrumb: { label: "Institution", icon: FontAwesomeIcons.Duotone.INSTITUTION, url: "/settings/institution" },
                navigation: { title: "Institution", description: "Manage site locations, key contacts, and institution types.", icon: FontAwesomeIcons.Duotone.INSTITUTION, drawer: true },
            },
            children: [
                {
                    index: true,
                    lazy: lazyRoute(() => import("../containers/Settings/InstitutionSettings/InstitutionSettingsPage")),
                    handle: {
                        title: "Institution",
                        help: { topicKey: "institution-settings" },
                        access: { permission: Dtos.Permission.InstitutionSettingsView },
                        breadcrumb: { label: "Institution", icon: FontAwesomeIcons.Duotone.INSTITUTION, url: "/settings/institution" },
                    }
                },
                {
                    path: "itemtemplates",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/InstitutionLookups/InstitutionItemTemplates/InstitutionItemTemplatesPage")),
                            handle: { help: { topicKey: "institution-item-templates" } }
                        },
                        {
                            path: ":institutionId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    lazy: lazyRoute(() => import("../containers/InstitutionLookups/InstitutionItemTemplates/InstitutionItemTemplatesPage")),
                                    handle: { help: { topicKey: "institution-item-templates" } }
                                },
                                {
                                    path: "template",
                                    handle: { help: null },
                                    children: [
                                        {
                                            path: ":institutionItemTemplateId",
                                            lazy: lazyRoute(() => import("../containers/InstitutionLookups/InstitutionItemTemplate/InstitutionItemTemplatePage")),
                                            handle: { help: { topicKey: "institution-item-template" } }
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "template",
                            handle: { help: null },
                            children: [
                                {
                                    path: ":institutionItemTemplateId",
                                    lazy: lazyRoute(() => import("../containers/InstitutionLookups/InstitutionItemTemplate/InstitutionItemTemplatePage")),
                                    handle: { help: { topicKey: "institution-item-template" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "itemcomponents",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponents/ItemComponentsPage"), { context: "institution" }),
                            handle: { help: { topicKey: "item-components" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "institution", mode: "create" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                        {
                            path: "create/:cloneId",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "institution", mode: "clone" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                        {
                            path: ":itemComponentId",
                            lazy: lazyRoute(() => import("../containers/Settings/ItemComponent/ItemComponentPageSWR"), { context: "institution", mode: "edit" }),
                            handle: { help: { topicKey: "item-component" } },
                        },
                    ],
                },
                {
                    path: "itemtemplatecategories",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/InstitutionLookups/InstitutionItemTemplateCategories/InstitutionItemTemplateCategoriesPage")),
                            handle: { help: { topicKey: "institution-item-template-categories" } }
                        },
                        {
                            path: ":institutionItemTemplateCategoryId",
                            lazy: lazyRoute(() => import("../containers/InstitutionLookups/InstitutionItemTemplateCategory/InstitutionItemTemplateCategoryPage")),
                            handle: { help: { topicKey: "institution-item-template-category" } }
                        },
                    ],
                },
                {
                    path: "sitelocations",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/InstitutionLookups/SiteLocations/SiteLocationsPage")),
                            handle: { help: { topicKey: "site-locations" } }
                        },
                        {
                            path: ":siteLocationId",
                            lazy: lazyRoute(() => import("../containers/InstitutionLookups/SiteLocation/SiteLocationPage")),
                            handle: { help: { topicKey: "site-location" } }
                        },
                    ],
                },
                {
                    path: "keycontacts",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/InstitutionLookups/KeyContacts/KeyContactsPage")),
                            handle: { help: { topicKey: "key-contacts" } }
                        },
                        {
                            path: ":keyContactId",
                            lazy: lazyRoute(() => import("../containers/InstitutionLookups/KeyContact/KeyContactPage")),
                            handle: { help: { topicKey: "key-contact" } }
                        },
                    ],
                },
                {
                    path: "institutiontypes",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/InstitutionLookups/InstitutionTypes/InstitutionTypesPage")),
                            handle: { help: { topicKey: "institution-types" } }
                        },
                        {
                            path: ":institutionTypeId",
                            lazy: lazyRoute(() => import("../containers/InstitutionLookups/InstitutionType/InstitutionTypePage")),
                            handle: { help: { topicKey: "institution-type" } }
                        },
                    ],
                },
                {
                    path: "institutioncodings",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/InstitutionLookups/InstitutionCodings/InstitutionCodingsPage")),
                            handle: { help: { topicKey: "institution-codings" } }
                        },
                        {
                            path: ":institutionCodingId",
                            lazy: lazyRoute(() => import("../containers/InstitutionLookups/InstitutionCoding/InstitutionCodingPage")),
                            handle: { help: { topicKey: "institution-coding" } }
                        },
                    ],
                },
            ],
        },
        {
            path: "hrecs",
            handle: {
                title: "HREC Manager",
                help: null,
                access: { permission: Dtos.Permission.HrecView },
                breadcrumb: { label: "HREC Manager", icon: FontAwesomeIcons.Duotone.HANDHOLDINGHEART, url: "/settings/hrecs" },
                navigation: { title: "HREC Manager", description: "Manage Human Research Ethics Committee (HREC) settings.", icon: FontAwesomeIcons.Duotone.HANDHOLDINGHEART, drawer: true },
            },
            children: [
                {
                    index: true,
                    lazy: lazyRoute(() => import("../containers/Settings/HREC/HrecsPage")),
                    handle: {
                        title: "HREC Manager",
                        help: { topicKey: "hrecs" },
                        access: { permission: Dtos.Permission.HrecView },
                        breadcrumb: { label: "HREC Manager", icon: FontAwesomeIcons.Duotone.HANDHOLDINGHEART, url: "/settings/hrecs" },
                    }
                },
                {
                    path: "create",
                    lazy: lazyRoute(() => import("../containers/Settings/HREC/HrecPage")),
                    handle: { help: { topicKey: "hrec" } }
                },
                {
                    path: ":hrecId",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            element: (
                                <Navigate replace to="details" />
                            ),
                            handle: { help: null }
                        },
                        {
                            path: "details",
                            lazy: lazyRoute(() => import("../containers/Settings/HREC/HrecPage")),
                            handle: { help: { topicKey: "hrec" } }
                        },
                    ],
                },
            ],
        },
        {
            path: "healthdistricts",
            handle: {
                title: "Health District Manager",
                help: null,
                access: { permission: Dtos.Permission.HealthDistrictView },
                breadcrumb: { label: "Health District Manager", icon: FontAwesomeIcons.Duotone.HOSPITALS, url: "/settings/healthdistricts" },
                navigation: { title: "Health District Manager", description: "Oversee health district configurations and related settings.", icon: FontAwesomeIcons.Duotone.HOSPITALS, drawer: true },
            },
            children: [
                {
                    index: true,
                    lazy: lazyRoute(() => import("../containers/Settings/HealthDistrict/HealthDistrictsPageSWR")),
                    handle: { help: { topicKey: "health-districts" } }
                },
                {
                    path: "create",
                    lazy: lazyRoute(() => import("../containers/Settings/HealthDistrict/HealthDistrictPageSWR")),
                    handle: { help: { topicKey: "health-district" } }
                },
                {
                    path: ":healthDistrictId",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            element: (
                                <Navigate replace to="details" />
                            ),
                            handle: { help: null }
                        },
                        {
                            path: "details",
                            lazy: lazyRoute(() => import("../containers/Settings/HealthDistrict/HealthDistrictPageSWR")),
                            handle: { help: { topicKey: "health-district" } }
                        },
                    ],
                },
            ],
        },
    ],
} satisfies AppRouteObject;

